import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import Dropdown from '../inputs/dropdown';
import TypeAhead from '../inputs/type-ahead';
import { konstPromise } from '../../utility/promise';
import { getDistinctValues } from './network';
const DATE_PRESETS = [
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'this_week', label: 'This Week' },
    { key: 'previous_week', label: 'Previous Week' },
    { key: 'this_month', label: 'This Month' },
    { key: 'last_30_days', label: 'Last 30 Days' },
    { key: 'this_quarter', label: 'This Quarter' },
    { key: 'last_3_months', label: 'Last 3 Months' },
    { key: 'year_to_date', label: 'Year to Date' },
    { key: 'previous_year', label: 'Previous Year' },
    { key: 'next_2_weeks', label: 'Next 2 Weeks' },
];
const CHECKBOX_OPTIONS = [
    { key: '__all__', label: 'All' },
    { key: 'true', label: 'Yes' },
    { key: 'false', label: 'No' },
];
const NUMBER_OPS = [
    { key: '=', label: '=' },
    { key: '!=', label: '!=' },
    { key: '>', label: '>' },
    { key: '<', label: '<' },
    { key: '>=', label: '>=' },
    { key: '<=', label: '<=' },
];
const datePresetsPromise = konstPromise(DATE_PRESETS);
const checkboxOptionsPromise = konstPromise(CHECKBOX_OPTIONS);
const numberOpsPromise = konstPromise(NUMBER_OPS);
/** TypeAhead + pills for multi-value text filters */
const TextFilterControl = ({ cfg, values, optionsPromise, onAdd, onRemove }) => {
    const [selected, setSelected] = React.useState(undefined);
    // Filter out already-selected values from the dropdown
    const filteredPromise = React.useMemo(() => optionsPromise.then(opts => opts.filter(o => !values.includes(o.key))), [optionsPromise, values]);
    return (_jsxs("div", { style: { minWidth: 200 }, children: [values.length > 0 && (_jsx("div", { className: "d-flex flex-wrap gap-1 mb-1", children: values.map(v => (_jsxs("span", { className: "badge bg-primary d-flex align-items-center gap-1", style: { fontSize: '0.7rem', fontWeight: 500 }, children: [v, _jsx("i", { className: "bi bi-x", style: { cursor: 'pointer', fontSize: '0.65rem' }, onClick: () => onRemove(v) })] }, v))) })), _jsx(TypeAhead, { label: cfg.label, value: selected, options: filteredPromise, optionKey: x => x.key, optionLabel: x => x.label, onChange: opt => {
                    if (opt) {
                        onAdd(opt.key);
                        setSelected(undefined);
                    }
                }, size: "sm" })] }));
};
const GlobalFilterBar = ({ configs, active, onChange }) => {
    // Cache distinct value promises by tableName+column
    const distinctCache = React.useRef(new Map());
    const getDistinctOptionsPromise = React.useCallback((tableName, column) => {
        const cacheKey = `${tableName}:${column}`;
        if (!distinctCache.current.has(cacheKey)) {
            distinctCache.current.set(cacheKey, getDistinctValues(tableName, column).then(values => (values || []).map(v => ({ key: v, label: v }))));
        }
        return distinctCache.current.get(cacheKey);
    }, []);
    const findActive = (cfg) => active.find(a => a.column === cfg.column && a.tableName === cfg.tableName);
    const updateActive = (cfg, patch) => {
        const existing = findActive(cfg);
        if (existing) {
            onChange(active.map(a => a.column === cfg.column && a.tableName === cfg.tableName
                ? Object.assign(Object.assign({}, a), patch) : a));
        }
        else {
            onChange([...active, Object.assign({ column: cfg.column, tableName: cfg.tableName, kind: cfg.kind }, patch)]);
        }
    };
    const clearActive = (cfg) => {
        onChange(active.filter(a => !(a.column === cfg.column && a.tableName === cfg.tableName)));
    };
    if (configs.length === 0) {
        return (_jsxs("div", { className: "d-flex align-items-center gap-2 text-muted small", children: [_jsx("i", { className: "bi bi-funnel" }), "No filters configured"] }));
    }
    return (_jsxs("div", { className: "d-flex flex-wrap align-items-end gap-2", children: [_jsxs("span", { className: "text-muted small fw-medium mb-2", children: [_jsx("i", { className: "bi bi-funnel me-1" }), "Filters:"] }), active.length > 0 && (_jsxs("button", { className: "btn btn-sm btn-outline-secondary mb-2", style: { lineHeight: 1, padding: '2px 6px' }, onClick: () => onChange([]), children: [_jsx("i", { className: "bi bi-x-lg me-1", style: { fontSize: '0.65rem' } }), "Clear"] })), configs.map(cfg => {
                var _a;
                const a = findActive(cfg);
                const kind = cfg.kind.toLowerCase();
                if (kind === 'date' || kind === 'datetime') {
                    return (_jsx("div", { style: { minWidth: 160 }, children: _jsx(Dropdown, { label: cfg.label, value: (a === null || a === void 0 ? void 0 : a.datePreset) || null, options: datePresetsPromise, optionKey: x => x.key, optionLabel: x => x.label, onChange: v => {
                                if (v)
                                    updateActive(cfg, { datePreset: v, values: undefined });
                                else
                                    clearActive(cfg);
                            }, size: "sm" }) }, `${cfg.tableName}:${cfg.column}`));
                }
                if (kind === 'checkbox') {
                    return (_jsx("div", { style: { minWidth: 120 }, children: _jsx(Dropdown, { label: cfg.label, value: ((_a = a === null || a === void 0 ? void 0 : a.values) === null || _a === void 0 ? void 0 : _a[0]) || null, options: checkboxOptionsPromise, optionKey: x => x.key, optionLabel: x => x.label, onChange: v => {
                                if (v && v !== '__all__')
                                    updateActive(cfg, { values: [v] });
                                else
                                    clearActive(cfg);
                            }, size: "sm" }) }, `${cfg.tableName}:${cfg.column}`));
                }
                if (kind === 'number') {
                    return (_jsxs("div", { className: "d-flex align-items-end gap-1", children: [_jsx("div", { style: { minWidth: 70 }, children: _jsx(Dropdown, { label: cfg.label, value: (a === null || a === void 0 ? void 0 : a.numberOp) || null, options: numberOpsPromise, optionKey: x => x.key, optionLabel: x => x.label, onChange: v => {
                                        if (v)
                                            updateActive(cfg, { numberOp: v });
                                        else
                                            clearActive(cfg);
                                    }, size: "sm" }) }), (a === null || a === void 0 ? void 0 : a.numberOp) && (_jsx("div", { style: { minWidth: 80 }, children: _jsxs("div", { className: "form-floating", children: [_jsx("input", { type: "number", className: "form-control form-control-sm", id: `num-${cfg.column}`, value: (a === null || a === void 0 ? void 0 : a.numberValue) || '', onChange: e => updateActive(cfg, { numberValue: e.target.value }), style: { minHeight: 58 } }), _jsx("label", { htmlFor: `num-${cfg.column}`, children: "Value" })] }) }))] }, `${cfg.tableName}:${cfg.column}`));
                }
                // Default: text/dropdown/textarea/select -- TypeAhead with pills
                const currentValues = (a === null || a === void 0 ? void 0 : a.values) || [];
                return (_jsx(TextFilterControl, { cfg: cfg, values: currentValues, optionsPromise: getDistinctOptionsPromise(cfg.tableName, cfg.column), onAdd: val => updateActive(cfg, { values: [...currentValues, val] }), onRemove: val => {
                        const next = currentValues.filter(v => v !== val);
                        if (next.length > 0)
                            updateActive(cfg, { values: next });
                        else
                            clearActive(cfg);
                    } }, `${cfg.tableName}:${cfg.column}`));
            })] }));
};
export default GlobalFilterBar;
